import {useQueryClient} from '@tanstack/react-query'
import moment from 'moment'
import {Timeline} from 'primereact/timeline'
import {useCallback, useMemo, useRef, useState} from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import {ReactComponent as FaUserInjured} from '../../assets/icons/FaUserInjured.svg'
import {ReactComponent as SymptomHistory} from '../../assets/images/history_symptom.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {QueryKeyTypes} from '../../query-manager/QueryKeyTypes'
import {QueryKeyManager} from '../../query-manager/QueryKeysManager'
import {useAuth} from '../authentication/AuthProvider'
import {isArrayAndNotEmpty} from '../observations/Utils'
import {UserPropertyType} from '../user-property/UserPropertyConstants'
import {getPageUserSymptoms} from '../user-property/UserPropertyEndPoint'
import {usePageUserSymptoms} from '../user-property/query-keys/UserPropertyQueryManager'

const InitialFilterRequest = {}
const InitialPageable = {
    pageRows: 10,
    pageNumber: 0,
    sortField: 'id',
    sortOrder: -1,
}

const PatientMedicalHistory = ({userId}) => {
    //#region Context
    const {user} = useAuth()
    //#endregion

    //#region Translate
    const {translateCommons} = TranslateUtils()
    //#endregion

    //#region State
    const [pageable, setPageable] = useState({...InitialPageable})
    const [filterData, setFilterData] = useState({
        ...InitialFilterRequest,
    })
    //#endregion

    const pageNumberRef = useRef(0)

    //#region Query
    const queryClient = useQueryClient()

    const {userPropertyKeys} = QueryKeyManager(
        QueryKeyTypes.userProperty,
        user?.id,
    )

    const userSymptomQueryKeys = useMemo(() => {
        return userPropertyKeys.page({
            type: UserPropertyType.userSymptom,
            pageNumber: pageable.pageNumber,
            pageRows: pageable.pageRows,
            sortField: pageable.sortField,
            sortOrder: pageable.sortOrder,
            filterRequest: {...filterData, userId: userId},
        })
    }, [
        userPropertyKeys,
        pageable.pageNumber,
        pageable.pageRows,
        pageable.sortField,
        pageable.sortOrder,
        filterData,
        userId,
    ])

    const {data: symptomData} = usePageUserSymptoms(
        user?.id,
        0,
        pageable.pageRows,
        pageable.sortField,
        pageable.sortOrder,
        {...filterData, userId: userId},
        true,
    )

    //#endregion

    const handleFetchMore = useCallback(async () => {
        if (pageNumberRef.current >= symptomData?.totalPages) {
            return
        }

        const nextSymptomData = await getPageUserSymptoms(
            user?.id,
            pageNumberRef.current + 1,
            pageable.pageRows,
            pageable.sortField,
            pageable.sortOrder,
            {...filterData, userId: userId},
        )

        if (isArrayAndNotEmpty(nextSymptomData?.content)) {
            const {userPropertyKeys} = QueryKeyManager(
                QueryKeyTypes.userProperty,
                user?.id,
            )
            queryClient.setQueryData(
                userPropertyKeys.page({
                    type: UserPropertyType.userSymptom,
                    pageNumber: 0,
                    pageRows: pageable.pageRows,
                    sortField: pageable.sortField,
                    sortOrder: pageable.sortOrder,
                    filterRequest: {...filterData, userId: userId},
                }),
                oldData => {
                    return {
                        ...oldData,
                        first: nextSymptomData?.first,
                        last: nextSymptomData?.last,
                        content: [
                            ...oldData.content,
                            ...nextSymptomData.content,
                        ],
                    }
                },
            )

            pageNumberRef.current++
        }
    }, [symptomData?.totalPages, user?.id, userId])

    const groupItems = useCallback(
        (array = symptomData?.content, property = 'createdAt') => {
            if (array && Array.isArray(array) && array.length > 0) {
                return array.reduce(function (groups, item) {
                    let name = moment(item[property]).format('MMMM Do YYYY')
                    let group = groups[name] || (groups[name] = [])
                    group.push(item)
                    return groups
                }, {})
            } else {
                return {}
            }
        },
        [symptomData],
    )

    const symptomsGroupData = useMemo(() => {
        let data = groupItems()
        return Object.keys(data).map(date => {
            return {
                date,
                data: data[date],
            }
        })
    }, [groupItems])

    const customizedMarker = item => {
        let isLatest = symptomData?.content[0].id === item.id
        return (
            <span
                className="flex w-2rem h-2rem align-items-center justify-content-center border-circle z-1 "
                style={{
                    backgroundColor: isLatest ? '#14b8a6' : '#ffffff',
                    border: `3px solid ${isLatest ? '#14b8a6' : '#d1d5db'}`,
                    transition: 'all 0.3s ease',
                }}>
                <i
                    className={`pi ${
                        isLatest ? 'pi-clock text-white' : 'pi-circle-fill'
                    }`}
                    style={{
                        fontSize: isLatest ? '0.8rem' : '0.5rem',
                        color: isLatest ? '#ffffff' : '#9ca3af',
                    }}></i>
            </span>
        )
    }

    const content = item => {
        let isLatest = symptomData?.content[0].id === item.id
        return (
            <div
                className={`mb-3  p-3 border-round-lg  transition-all transition-duration-300 hover:shadow-2 cursor-pointer ${
                    isLatest
                        ? 'border-1 border-teal-200 '
                        : 'surface-card border-1 border-gray-100 hover:border-gray-200'
                }`}>
                <div className="flex align-items-center justify-content-between mb-2">
                    <span
                        className={`text-sm font-semibold ${
                            isLatest ? 'text-teal-700' : 'text-600'
                        }`}>
                        {moment(item.createdAt).format('H:mm A')}
                    </span>
                    {isLatest && (
                        <span className="bg-teal-500 text-white px-2 py-1 border-round text-xs font-semibold">
                            Latest
                        </span>
                    )}
                </div>
                <div
                    className={`line-height-3 ${
                        isLatest ? 'text-black font-medium' : 'text-700'
                    }`}>
                    {translateCommons(item.symptom)}
                </div>
            </div>
        )
    }

    return (
        <div className="h-full">
            {/* Header */}
            <div className="surface-card border-round-lg p-0     mb-3 shadow-1">
                <div className="flex align-items-center justify-content-center">
                    <div className="flex align-items-center gap-3">
                        <div className="flex align-items-center justify-content-center w-3rem h-3rem bg-gray-50 border-circle">
                            <FaUserInjured
                                style={{width: '20px', height: '20px'}}
                                className="text-teal-600"
                            />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-700 m-0">
                                {translateCommons('Symptom History')}
                            </h3>
                            <p className="text-sm text-500 m-0 mt-1">
                                Patient medical timeline
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div
                className="surface-card border-round-lg p-4 shadow-1"
                style={{
                    height: 'calc(100% - 120px)',
                    overflow: 'hidden',
                }}>
                <div
                    className="h-full"
                    style={{
                        maxHeight: '100vh',
                        overflowY: 'scroll',
                        paddingRight: '8px',
                    }}>
                    {!symptomsGroupData || symptomsGroupData?.length < 1 ? (
                        <div className="h-full flex flex-column justify-content-center align-items-center text-center">
                            <div className="mb-4 opacity-60">
                                <SymptomHistory height={'120px'} />
                            </div>
                            <div className="max-w-20rem">
                                <h4 className="text-600 font-semibold mb-2">
                                    No History Available
                                </h4>
                                <p className="text-500 text-sm line-height-3 m-0">
                                    {translateCommons(
                                        'There is currently no medical history of symptoms for this patient.',
                                    )}
                                </p>
                            </div>
                        </div>
                    ) : (
                        <InfiniteScroll
                            style={{
                                height: 'max-content',
                            }}
                            pageStart={pageable?.pageNumber || 0}
                            loadMore={handleFetchMore}
                            hasMore={!symptomData?.last}
                            loader={
                                symptomData &&
                                !!symptomData?.last && (
                                    <div className="text-center p-3" key={0}>
                                        <div className="inline-flex align-items-center gap-2 text-600">
                                            <i className="pi pi-spin pi-spinner"></i>
                                            <span className="text-sm">
                                                {translateCommons('Loading')}...
                                            </span>
                                        </div>
                                    </div>
                                )
                            }
                            useWindow={false}>
                            {symptomsGroupData.map((item, index) => (
                                <div className="mb-4" key={item.date}>
                                    {/* Date Header */}
                                    <div className="flex align-items-center gap-3 mb-3">
                                        <div className="flex align-items-center gap-2 bg-gray-100 px-3 py-2 border-round-lg">
                                            <i className="pi pi-calendar text-black text-sm"></i>
                                            <span className="font-semibold text-black text-sm">
                                                {item.date}
                                            </span>
                                        </div>
                                        <div className="flex-1 h-1px bg-gray-200"></div>
                                    </div>

                                    {/* Timeline */}
                                    <div className="ml-2">
                                        <Timeline
                                            value={item.data}
                                            align="left"
                                            content={content}
                                            marker={customizedMarker}
                                            className="custom-timeline"
                                        />
                                    </div>
                                </div>
                            ))}
                        </InfiniteScroll>
                    )}
                </div>
            </div>

            <style jsx>{`
                .custom-timeline .p-timeline-event-connector {
                    background-color: #e5e7eb;
                    width: 2px;
                }
                .custom-timeline .p-timeline-event-content {
                    padding-left: 1.5rem;
                }
                .custom-timeline
                    .p-timeline-event:last-child
                    .p-timeline-event-connector {
                    display: none;
                }
            `}</style>
        </div>
    )
}

export default PatientMedicalHistory
